import { useEffect, useRef, useState, useMemo } from 'react';
import { CheckCircle2, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PlaybookDemoScriptLine } from '@/types/playbook/playbook';

interface PlaybookConversationDemoProps {
  title?: string;
  subtitle?: string;
  demoScript: PlaybookDemoScriptLine[];
  benefits?: string[];
  playbookName: string;
}

const MESSAGE_INTERVAL = 2500;
const TYPING_DURATION = 1200;

export const PlaybookConversationDemo = ({
  title = 'See it in action',
  subtitle = 'Watch how your voice genie handles a real conversation — naturally and effectively.',
  demoScript,
  benefits = [],
  playbookName,
}: PlaybookConversationDemoProps) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [started, setStarted] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Messages with computed delays
  const messages = useMemo(() => {
    return demoScript.map((line, idx) => ({
      speaker: line.speaker,
      text: line.message,
      delay: idx * MESSAGE_INTERVAL,
    }));
  }, [demoScript]);

  // Benefits with computed delays (appear after messages)
  const benefitItems = useMemo(() => {
    const baseDelay = messages.length * MESSAGE_INTERVAL + 1000;
    return benefits.slice(0, 5).map((b, idx) => ({
      text: b,
      delay: baseDelay + idx * 600,
    }));
  }, [benefits, messages.length]);

  const totalDuration = useMemo(() => {
    if (benefitItems.length > 0) {
      return benefitItems[benefitItems.length - 1].delay + 3000;
    }
    return messages.length * MESSAGE_INTERVAL + 3000;
  }, [benefitItems, messages.length]);

  // Scroll visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, [isInView]);

  // Animation loop
  useEffect(() => {
    if (!isInView) return;

    let elapsed = 0;
    setStarted(true);
    setVisibleCount(0);

    const tick = () => {
      elapsed += 200;

      // Count visible messages
      const count = messages.filter((m) => elapsed >= m.delay).length;
      setVisibleCount(count);

      // Show typing indicator before next genie message
      const nextMsg = messages[count];
      if (
        nextMsg &&
        nextMsg.speaker === 'agent' &&
        elapsed >= nextMsg.delay - TYPING_DURATION &&
        elapsed < nextMsg.delay
      ) {
        setIsTyping(true);
      } else {
        setIsTyping(false);
      }

      // Loop
      if (elapsed < totalDuration) {
        timerRef.current = setTimeout(tick, 200);
      } else {
        // Restart after a pause
        timerRef.current = setTimeout(() => {
          elapsed = 0;
          setVisibleCount(0);
          setIsTyping(false);
          timerRef.current = setTimeout(tick, 200);
        }, 4000);
      }
    };

    timerRef.current = setTimeout(tick, 1500);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isInView, messages, totalDuration]);

  const handleReplay = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisibleCount(0);
    setIsTyping(false);
    setStarted(false);
    setTimeout(() => setStarted(true), 100);
  };

  // Benefits visibility
  const now = visibleCount * MESSAGE_INTERVAL;
  const visibleBenefits = benefitItems.filter((b) => now >= b.delay - messages.length * MESSAGE_INTERVAL + messages.length * MESSAGE_INTERVAL);

  if (!demoScript || demoScript.length === 0) return null;

  return (
    <section ref={sectionRef} className="relative w-full overflow-hidden pt-16 lg:pt-24 pb-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight tracking-tight">
            {title}
          </h2>
          <p className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto">{subtitle}</p>
        </div>

        {/* Two column layout: Chat | Outcomes */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8 lg:gap-10 items-start max-w-5xl mx-auto">
          {/* Chat bubbles */}
          <div className="min-h-[400px] flex flex-col justify-start">
            {started && (
              <div className="flex flex-col gap-3">
                {messages.slice(0, visibleCount).map((message, index) => (
                  <div
                    key={index}
                    className={cn(
                      'flex items-end gap-2',
                      message.speaker === 'user' ? 'justify-end' : 'justify-start',
                      'animate-fade-slide-in'
                    )}
                  >
                    {message.speaker === 'agent' && (
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white shadow-md overflow-hidden">
                        <img src="/genie-mascots/role/support.png" alt="Genie" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div
                      className={cn(
                        'px-4 py-3 rounded-2xl shadow-lg',
                        'text-sm lg:text-base leading-relaxed',
                        message.speaker === 'user'
                          ? 'bg-white text-gray-800 rounded-br-sm border border-gray-100 max-w-[85%]'
                          : 'bg-gradient-to-r from-[#4E9CFF] to-[#28406F] text-white rounded-bl-sm max-w-[calc(85%-40px)]'
                      )}
                    >
                      {message.text}
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex items-end gap-2 justify-start animate-fade-slide-in">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white shadow-md overflow-hidden">
                      <img src="/genie-mascots/role/support.png" alt="Genie" className="w-full h-full object-cover" />
                    </div>
                    <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-gradient-to-r from-[#4E9CFF] to-[#28406F] shadow-lg">
                      <div className="flex gap-1.5">
                        <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '600ms' }} />
                        <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '150ms', animationDuration: '600ms' }} />
                        <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '300ms', animationDuration: '600ms' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!started && (
              <div className="flex flex-col gap-3 opacity-30">
                <div className="flex justify-end">
                  <div className="px-4 py-3 rounded-2xl rounded-br-sm bg-gray-100 text-gray-400 max-w-[80%]">
                    Customer message appears here...
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-gray-200 text-gray-400 max-w-[80%]">
                    Genie response appears here...
                  </div>
                </div>
              </div>
            )}

            {/* Replay button */}
            <button
              onClick={handleReplay}
              className="mt-4 flex items-center gap-1.5 text-gray-400 hover:text-gray-600 transition-colors text-xs self-start"
            >
              <RotateCcw size={14} />
              <span>Replay</span>
            </button>
          </div>

          {/* Outcomes sidebar */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              {playbookName} delivers
            </p>
            {benefits.slice(0, 5).map((benefit, idx) => {
              const isVisible = visibleCount >= messages.length - 1 && idx <= visibleCount - messages.length + 2;
              return (
                <div
                  key={idx}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-400 ease-out mb-2.5',
                    isVisible || visibleCount >= messages.length
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 translate-x-4 pointer-events-none'
                  )}
                >
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{benefit}</span>
                </div>
              );
            })}

            {(!started || visibleCount < 2) && benefits.length === 0 && (
              <div className="flex flex-col gap-2.5 opacity-30">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-100">
                    <div className="w-5 h-5 rounded-full bg-gray-200" />
                    <div className="h-4 w-40 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-slide-in {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-slide-in {
          animation: fade-slide-in 0.25s ease-out forwards;
        }
      `}</style>
    </section>
  );
};
